import { EventUser } from 'services/signaling';

export function isPoll(poll: unknown): poll is Poll {
  return poll !== null && typeof poll === 'object' && 'choices' in poll;
}

export type PollType = 'single' | 'multiple' | 'free';

export interface PollChoice {
  id: string;
  text: string;
  votersCount: number;
}

export interface Poll {
  id: string;
  sessionId: string;
  question: string;
  type: PollType;
  anonymous: boolean;
  active: boolean;
  ended: boolean;
  shared: boolean;
  editable: boolean;
  votersCount: number;
  voted?: number[] | string;
  votes?: DetailedResultAnswer[];
  user: EventUser | null;
  choices?: PollChoice[];
}

export type PollEntities = Record<string, Poll>;
export type PollIds = string[];

export type VotedPoll = Record<string, boolean>;

export type LocalVotedPoll = VotedPoll | string;

export type DetailedResultVotes = Record<string, EventUser[]>;

export interface DetailedResultAnswer {
  voter?: EventUser;
  answer: string;
}

export interface DetailedResult {
  status: 'idle' | 'loading' | 'success' | 'error';
  votes?: DetailedResultVotes;
  answers?: DetailedResultAnswer[];
}

export type LocalVotedPolls = Record<string, LocalVotedPoll>;

export interface PollTemplateChoice {
  id: string;
  text: string;
}

export interface PollTemplate {
  id: string;
  question: string;
  type: PollType;
  choices?: PollTemplateChoice[];
  anonymous: boolean;
}

export type PollTemplateEntities = Record<string, PollTemplate>;
export type PollTemplateIds = string[];

export interface PollsState {
  exportProcessing: boolean;
  activePollId: string | null;
  activePollSessionId: string | null;
  entities: PollEntities;
  ids: PollIds;
  localVotedPolls: LocalVotedPolls;
  detailedResults: Record<string, DetailedResult>;
  templates: {
    entities: PollTemplateEntities;
    ids: PollTemplateIds;
  };
}

export interface PollPayload {
  question: string;
  type: PollType;
  choices?: string[];
  anonymous: boolean;
}

export interface EditPollPayload extends PollPayload {
  id: string;
}

export interface VotePayload {
  id: string;
  choices?: number[];
  answer?: string;
}

export interface VoteResponse extends VotePayload {
  local: boolean;
  voter?: EventUser;
}

export interface DetailedPollResultVote {
  voter: EventUser;
  answer?: string;
  choices?: number[];
}

export interface DetailedPollResultsPayload {
  id: string;
  votes: DetailedPollResultVote[];
}

export interface CreatePollTemplatePayload {
  question: string;
  type: PollType;
  choices: string[];
  anonymous: boolean;
}

export interface EditPollTemplatePayload extends CreatePollTemplatePayload {
  id: string;
}
